import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Popover,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  CheckCircleOutline,
  ErrorOutline,
  Refresh,
  Sync,
  Wifi,
  WifiOff,
} from '@mui/icons-material';
import { useOfflineContext } from 'src/offline';
import { useWsContext } from 'src/websocket';
import { fDateTime } from 'src/utils/format-time';

export interface SyncIndicatorProps {
  onForceSync?: () => Promise<void> | void;
}

type DisplayColor = 'success' | 'warning' | 'info' | 'error';

const formatLatency = (value: number | null) => (value === null ? '—' : `${value} ms`);
const formatDate = (value: Date | null, fallback: string) =>
  value ? fDateTime(value.toISOString()) : fallback;

function StatusRow({
  label,
  ok,
  value,
  warning = false,
}: {
  label: string;
  ok: boolean;
  value: string;
  warning?: boolean;
}) {
  return (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ py: 0.45 }}>
      {ok ? (
        <CheckCircleOutline sx={{ fontSize: 18, color: warning ? 'warning.main' : 'success.main' }} />
      ) : (
        <ErrorOutline sx={{ fontSize: 18, color: 'error.main' }} />
      )}
      <Typography variant="body2" sx={{ minWidth: 120 }}>
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={600} sx={{ flex: 1, textAlign: 'right' }}>
        {value}
      </Typography>
    </Stack>
  );
}

function MetricRow({ label, value }: { label: string; value: string }) {
  return (
    <Stack direction="row" justifyContent="space-between" spacing={2} sx={{ py: 0.4 }}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="caption" fontWeight={600} sx={{ textAlign: 'right' }}>
        {value}
      </Typography>
    </Stack>
  );
}

export function SyncIndicator({ onForceSync }: SyncIndicatorProps) {
  const { t } = useTranslation();
  const ws = useWsContext();
  const {
    network,
    isSyncing,
    sync,
    lastSyncAt,
    hasPendingItems,
  } = useOfflineContext();
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const [manualSyncing, setManualSyncing] = useState(false);

  const wsConnected = ws.connectionStatus === 'connected';
  const wsWarning = !wsConnected || ws.missedHeartbeats >= 2;

  const qualityMap: Record<
    typeof network.quality,
    { label: string; color: DisplayColor }
  > = {
    good: { label: t('sync.network.quality.good'), color: 'success' },
    fair: { label: t('sync.network.quality.fair'), color: 'warning' },
    degraded: { label: t('sync.network.quality.degraded'), color: 'warning' },
    offline: { label: t('sync.network.quality.offline'), color: 'error' },
    unknown: { label: t('sync.network.quality.unknown'), color: 'info' },
  };

  const qualityDisplay = qualityMap[network.quality];
  const chipColor: DisplayColor = network.isOffline
    ? 'error'
    : wsWarning
      ? 'warning'
      : qualityDisplay.color;

  const chipLabel = network.isOffline
    ? t('sync.network.badge_offline')
    : wsWarning && network.apiReachable
      ? t('sync.network.badge_ws_issue')
      : t('sync.network.badge', {
          quality: qualityDisplay.label,
          latency: network.latencyMs === null ? '—' : network.latencyMs,
        });

  const handleForceSync = async () => {
    if (manualSyncing || isSyncing || network.isOffline) return;
    setManualSyncing(true);
    try {
      if (onForceSync) await onForceSync();
      else await sync({ retryFailed: true });
    } finally {
      setManualSyncing(false);
    }
  };

  const syncing = manualSyncing || isSyncing;
  const noDate = t('sync.network.never');

  return (
    <>
      <Tooltip title={t('sync.network.open_details')} arrow placement="bottom">
        <Chip
          icon={
            syncing ? (
              <CircularProgress size={14} />
            ) : network.isOffline ? (
              <WifiOff sx={{ fontSize: 18 }} />
            ) : (
              <Wifi sx={{ fontSize: 18 }} />
            )
          }
          label={chipLabel}
          color={chipColor}
          size="small"
          onClick={(event) => setAnchorEl(event.currentTarget)}
          sx={{ cursor: 'pointer', maxWidth: 240 }}
        />
      </Tooltip>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { width: 410, maxWidth: 'calc(100vw - 24px)' } } }}
      >
        <Box sx={{ p: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Wifi sx={{ color: 'primary.main' }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle1" fontWeight={700}>
                {t('sync.network.title')}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t('sync.network.quality_label')}: {qualityDisplay.label}
              </Typography>
            </Box>
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                bgcolor: `${chipColor}.main`,
              }}
            />
          </Stack>

          <Divider sx={{ my: 1.5 }} />

          <StatusRow
            label={t('sync.network.internet')}
            ok={network.browserOnline}
            value={network.browserOnline ? t('sync.network.available') : t('sync.network.unavailable')}
          />
          <StatusRow
            label={t('sync.network.api')}
            ok={network.apiReachable}
            warning={!network.apiReachable && !network.isOffline}
            value={
              network.apiReachable
                ? `${t('sync.network.available')} · ${formatLatency(network.latencyMs)}`
                : network.isOffline
                  ? t('sync.network.unavailable')
                  : t('sync.network.transient_failure')
            }
          />
          <StatusRow
            label={t('sync.network.websocket')}
            ok={wsConnected}
            warning={ws.missedHeartbeats > 0}
            value={
              wsConnected
                ? `${t('sync.connected')} · ${formatLatency(ws.wsLatencyMs)}`
                : t(`sync.${ws.connectionStatus}`)
            }
          />

          <Divider sx={{ my: 1.5 }} />

          <MetricRow label={t('sync.network.api_latency')} value={formatLatency(network.latencyMs)} />
          <MetricRow label={t('sync.network.ws_latency')} value={formatLatency(ws.wsLatencyMs)} />
          <MetricRow label={t('sync.network.jitter')} value={formatLatency(network.jitterMs)} />
          <MetricRow label={t('sync.network.failure_rate')} value={`${network.failureRate} %`} />
          <MetricRow
            label={t('sync.network.reconnects')}
            value={String(ws.reconnectCount)}
          />
          <MetricRow
            label={t('sync.network.missed_heartbeats')}
            value={String(ws.missedHeartbeats)}
          />
          {network.browserConnection.effectiveType && (
            <MetricRow
              label={t('sync.network.browser_type')}
              value={network.browserConnection.effectiveType.toUpperCase()}
            />
          )}
          {network.browserConnection.downlinkMbps !== undefined && (
            <MetricRow
              label={t('sync.network.browser_downlink')}
              value={`${network.browserConnection.downlinkMbps} Mbps`}
            />
          )}

          <Divider sx={{ my: 1.5 }} />

          <MetricRow
            label={t('sync.network.last_test')}
            value={formatDate(network.lastCheckedAt, noDate)}
          />
          <MetricRow
            label={t('sync.network.last_api_success')}
            value={formatDate(network.lastSuccessfulAt, noDate)}
          />
          <MetricRow
            label={t('sync.network.last_pong')}
            value={formatDate(ws.lastPongAt, noDate)}
          />
          <MetricRow
            label={t('sync.network.last_sync')}
            value={formatDate(lastSyncAt, noDate)}
          />
          {hasPendingItems && (
            <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'warning.main' }}>
              {t('sync.network.pending_orders')}
            </Typography>
          )}

          <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<Refresh />}
              onClick={() => void network.checkConnection()}
              fullWidth
            >
              {t('sync.network.test_now')}
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<Sync />}
              onClick={ws.reconnect}
              fullWidth
            >
              {t('sync.network.reconnect_ws')}
            </Button>
          </Stack>
          <Button
            variant="contained"
            size="small"
            startIcon={syncing ? <CircularProgress size={15} /> : <Sync />}
            onClick={handleForceSync}
            disabled={syncing || network.isOffline}
            fullWidth
            sx={{ mt: 1 }}
          >
            {syncing ? t('sync.health.syncing') : t('sync.force_sync')}
          </Button>
        </Box>
      </Popover>
    </>
  );
}

export default SyncIndicator;
